import { useState } from "react";
import { omit } from "lodash";
import { Controller, useForm } from "react-hook-form";
import { useModal } from "react-modal-hook";
import { CUSTOM_MAP_TYPE, MIME_TYPE } from "common/constants";
import {
  useCreateCustomMapMutation,
  useGetPresignedCustomMapGeojsonUploadUrlLazyQuery,
  useUpdateCustomMapMutation,
} from "../../../../generated/graphql";
import { useStatusToasts } from "../../../../hooks/useStatusToasts";
import { track } from "../../../../utils/tracking";
import { CommonModal } from "../../../Common/CommonModal";
import Modal from "../../../Common/Modal";
import { Switch } from "../../../Inputs";
import {
  Select,
  SingleFileUpload,
  Text,
} from "../../../Inputs/react-hook-form";
import { ReactHookFormTextInput as TextInput } from "../../../Inputs/Text";
import { InputGrid } from "../PropertyData/__styles__/floodInfoModals";
import {
  SwitchContainer,
  SwitchLabel,
  SwitchRow,
  UpsertModalContent,
} from "./__styles__/UpsertCustomMapModal";
import { CustomMapForSettings } from "./types";

const CUSTOM_MAP_TYPE_OPTIONS = [
  { value: CUSTOM_MAP_TYPE.INCLUSION_EXCLUSION, label: "Inclusion/Exclusion" },
  { value: CUSTOM_MAP_TYPE.VISUAL, label: "Visual" },
  { value: CUSTOM_MAP_TYPE.MULTIPLE_INCLUSIONS, label: "Multiple Inclusions" },
  { value: CUSTOM_MAP_TYPE.GREATEST_INCLUSION, label: "Greatest Inclusion" },
];

interface UpsertGeomCustomMapForm {
  name: string;
  allowsCreation: boolean;
  enabled: boolean;
  displayOrder: number;
  hiddenFromPublic: boolean;
  isDefault: boolean;
  labelsVisibleOnMap: boolean;
  legendEnabled: boolean;
  type: CUSTOM_MAP_TYPE;
  isObject: boolean;
  geojsonFile: { blob: Blob; originalFilename: string };
}

interface UpsertGeomCustomMapModalProps {
  customMap: Maybe<CustomMapForSettings>;
  hideModal: () => void;
  onSave?: () => void;
}

export const UpsertGeomCustomMapModal = ({
  customMap,
  hideModal,
  onSave,
}: UpsertGeomCustomMapModalProps) => {
  const { addSuccessToast, addErrorToast } = useStatusToasts();
  const [uploading, setUploading] = useState(false);

  const [getPresignedUrl] = useGetPresignedCustomMapGeojsonUploadUrlLazyQuery({
    fetchPolicy: "no-cache",
  });

  const [createCustomMap, { loading: createLoading }] =
    useCreateCustomMapMutation();

  const [updateCustomMap, { loading: updateLoading }] =
    useUpdateCustomMapMutation();

  const isEdit = !!customMap;

  const formMethods = useForm<UpsertGeomCustomMapForm>({
    defaultValues:
      customMap ?
        omit(customMap, ["canDelete", "geometryCount", "__typename"])
      : {
          allowsCreation: false,
          isObject: false,
          enabled: false,
          hiddenFromPublic: true,
          isDefault: false,
          labelsVisibleOnMap: false,
          legendEnabled: false,
          displayOrder: 0,
        },
    mode: "onChange",
  });

  const {
    control,
    handleSubmit,
    register,
    formState: { errors, isValid },
  } = formMethods;

  const onSubmit = async (formData: UpsertGeomCustomMapForm) => {
    if (isEdit) {
      await onUpdate(formData);
    } else {
      await onCreate(formData);
    }
  };

  const onCreate = async (formData: UpsertGeomCustomMapForm) => {
    try {
      setUploading(true);

      const { data: presignedData } = await getPresignedUrl();
      if (!presignedData) {
        throw new Error("Failed to get presigned upload URL");
      }

      const { s3Key, url } =
        presignedData.getPresignedCustomMapGeojsonUploadUrl;

      const uploadResult = await fetch(url, {
        method: "PUT",
        body: formData.geojsonFile.blob,
      });

      if (!uploadResult.ok) {
        throw new Error(
          `Received a ${uploadResult.status} when uploading file`,
        );
      }

      await createCustomMap({
        variables: {
          data: {
            ...omit(formData, ["geojsonFile"]),
            s3Key,
          },
        },
        onCompleted: () => {
          track("Created custom map", { name: formData.name });
          addSuccessToast("Custom map created successfully");
          onSave?.();
          hideModal();
        },
        onError: () => {
          addErrorToast(
            "There was an issue creating the custom map. Please try again.",
          );
        },
      });
    } catch {
      addErrorToast("There was an issue uploading the file. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const onUpdate = async (formData: UpsertGeomCustomMapForm) => {
    let news3Key: string | undefined;
    if (formData.geojsonFile?.blob) {
      setUploading(true);

      const { data: presignedData } = await getPresignedUrl();
      if (!presignedData) {
        throw new Error("Failed to get presigned upload URL");
      }

      const { s3Key, url } =
        presignedData.getPresignedCustomMapGeojsonUploadUrl;
      news3Key = s3Key;

      const uploadResult = await fetch(url, {
        method: "PUT",
        body: formData.geojsonFile.blob,
      });

      if (!uploadResult.ok) {
        throw new Error(
          `Received a ${uploadResult.status} when uploading file`,
        );
      }
    }

    await updateCustomMap({
      variables: {
        data: {
          id: customMap!.id,
          ...omit(formData, ["geojsonFile"]),
          s3Key: news3Key,
        },
      },
      onCompleted: () => {
        track("Updated custom map", { name: formData.name });
        addSuccessToast("Custom map updated successfully");
        onSave?.();
        hideModal();
      },
      onError: () => {
        addErrorToast(
          "There was an issue updating the custom map. Please try again.",
        );
      },
    });
  };

  const verb = isEdit ? "Update" : "Add";

  return (
    <CommonModal
      title={`${verb} custom map`}
      primaryButtonText={verb}
      onSubmit={handleSubmit(formData => void onSubmit(formData))}
      disabled={!isValid}
      loading={uploading || createLoading || updateLoading}
      onCancel={hideModal}
      size="small"
      content={
        <UpsertModalContent>
          <InputGrid>
            <Text
              label="Name of custom map"
              placeholder="Name"
              error={errors.name?.message}
              {...register("name", {
                required: "Name is required",
              })}
              required
            />
            <Select
              control={control}
              name="type"
              label="Type"
              options={CUSTOM_MAP_TYPE_OPTIONS}
              size="medium"
              required
              menuPortalTarget={document.body}
              styles={{
                menuPortal: base => ({
                  ...base,
                  zIndex: 9999,
                }),
              }}
            />
            <SingleFileUpload
              control={control}
              name="geojsonFile"
              label="GeoJSON file"
              allowedMimeTypes={[MIME_TYPE.GEOJSON]}
              rules={{ required: !isEdit && "GeoJSON file is required" }}
              formMethods={formMethods}
              required={!isEdit}
              compact
              hasImage={false}
            />
            <TextInput
              {...register("displayOrder", {
                required: "Display order is required",
              })}
              name="displayOrder"
              label="Display order"
              type="number"
              size="medium"
              required
            />
            <Controller
              control={control}
              name="hiddenFromPublic"
              render={({ field }) => (
                <SwitchRow>
                  <SwitchContainer>
                    <Switch
                      name="hiddenFromPublic"
                      value={field.value}
                      onChange={field.onChange}
                      size="small"
                    />
                  </SwitchContainer>
                  <SwitchLabel>Hidden from public</SwitchLabel>
                </SwitchRow>
              )}
            />
            <Controller
              control={control}
              name="isDefault"
              render={({ field }) => (
                <SwitchRow>
                  <SwitchContainer>
                    <Switch
                      name="isDefault"
                      value={field.value}
                      onChange={field.onChange}
                      size="small"
                    />
                  </SwitchContainer>
                  <SwitchLabel>Default</SwitchLabel>
                </SwitchRow>
              )}
            />
            <Controller
              control={control}
              name="labelsVisibleOnMap"
              render={({ field }) => (
                <SwitchRow>
                  <SwitchContainer>
                    <Switch
                      name="labelsVisibleOnMap"
                      value={field.value}
                      onChange={field.onChange}
                      size="small"
                    />
                  </SwitchContainer>
                  <SwitchLabel>Labels visible on map</SwitchLabel>
                </SwitchRow>
              )}
            />
            <Controller
              control={control}
              name="legendEnabled"
              render={({ field }) => (
                <SwitchRow>
                  <SwitchContainer>
                    <Switch
                      name="legendEnabled"
                      value={field.value}
                      onChange={field.onChange}
                      size="small"
                    />
                  </SwitchContainer>
                  <SwitchLabel>Legend enabled</SwitchLabel>
                </SwitchRow>
              )}
            />
          </InputGrid>
        </UpsertModalContent>
      }
    />
  );
};

export const useUpsertGeomCustomMapModal = ({
  onSave,
}: {
  onSave?: () => void;
}) => {
  const [customMap, setCustomMap] = useState<Maybe<CustomMapForSettings>>(null);

  const [showModal, hideModal] = useModal(
    () => (
      <Modal onRequestClose={hideModal}>
        <UpsertGeomCustomMapModal
          hideModal={hideModal}
          onSave={onSave}
          customMap={customMap}
        />
      </Modal>
    ),
    [onSave],
  );

  const showUpsertModal = (map?: CustomMapForSettings) => {
    setCustomMap(map ?? null);
    showModal();
  };

  return [showUpsertModal, hideModal] as const;
};
